import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

// Top-level error boundary. Catches uncaught render errors from any route
// and shows a minimal "something went wrong" card instead of the blank
// white page you get from an unhandled render throw. Reload button is the
// only action — we don't try to recover in place, because the underlying
// component tree already threw and React treats it as destroyed.
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Send to console so Sentry's default console integration picks it up
    // if Sentry is wired. Even without Sentry this shows in browser devtools.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: 'var(--color-bg)',
          color: 'var(--color-primary)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            maxWidth: 360,
            padding: '24px 20px',
            borderRadius: 14,
            background: 'var(--color-surface)',
          }}
        >
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 8px', letterSpacing: '-0.01em' }}>
            Something went wrong
          </h1>
          <p
            style={{
              margin: '0 0 20px',
              fontSize: 14,
              color: 'var(--color-text-secondary)',
              lineHeight: 1.5,
            }}
          >
            We hit an unexpected error. Reloading usually fixes it.
          </p>
          <button
            onClick={this.handleReload}
            className="btn-primary"
            style={{ width: '100%' }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
